import React from 'react';
import { Link } from 'react-router-dom';
import { PenTool, Zap, Check, Clock, Layout, ArrowRight, Palette, Layers, RefreshCw, PlusCircle, Briefcase, Shield } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const DesignersUseCase = () => {
  // Benefits data
  const benefits = [
    {
      icon: Clock,
      title: "Focus on High-Value Tasks",
      description: "Let AI handle the initial layouts and repetitive elements while you focus on the creative aspects that truly require your expertise."
    },
    {
      icon: Palette,
      title: "Explore More Design Options",
      description: "Generate multiple design alternatives in minutes to explore different approaches and find the most effective solution."
    },
    {
      icon: RefreshCw,
      title: "Accelerate Iteration Cycles",
      description: "Quickly implement feedback and create new versions without starting from scratch each time."
    },
    {
      icon: Layers,
      title: "Build Complete User Flows",
      description: "Effortlessly create and connect multiple screens to demonstrate full user journeys and interactions."
    }
  ];

  // Professional growth benefits - implied psychological benefits
  const professionalBenefits = [
    {
      icon: Shield,
      title: "Creative Vision Integrity",
      description: "Share fully interactive prototypes that accurately represent your design intent, ensuring your creative vision is understood exactly as you imagined it."
    },
    {
      icon: Clock,
      title: "Design Under Pressure",
      description: "Meet tight deadlines with confidence by rapidly generating complete designs when time constraints would normally limit your output or creative exploration."
    },
    {
      icon: RefreshCw,
      title: "Real-time Adaptation",
      description: "Make design changes during live stakeholder meetings, transforming feedback sessions from passive reviews to active collaboration moments."
    },
    {
      icon: Briefcase,
      title: "Professional Impact",
      description: "Demonstrate your efficiency and technical competence by delivering more comprehensive design work in less time, enhancing your professional reputation."
    }
  ];

  // Use case scenarios
  const scenarios = [
    {
      title: "Rapid Prototyping for Client Presentations",
      description: "Generate multiple design directions based on client briefs to show a range of options in your next presentation."
    },
    {
      title: "Design System Exploration",
      description: "Quickly visualize how different design system choices affect your interfaces before committing to implementation."
    },
    {
      title: "User Testing Preparation",
      description: "Create interactive prototypes for user testing sessions without spending days building them manually."
    },
    {
      title: "Responsive Variant Creation",
      description: "Generate mobile, tablet, and desktop versions of your designs to ensure consistent experiences across devices."
    }
  ];

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-6">Designed for Designers</h1>
              <p className="text-xl mb-8 opacity-90">
                Amplify your creativity and productivity with AI-powered design tools that help you explore more ideas and deliver faster.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Generate multiple design variations in minutes, not hours</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Focus on creative direction rather than implementation details</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Create interactive prototypes without coding knowledge</p>
                </div>
              </div>
              <Link
                to="/signup"
                className="inline-block px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Start Free Trial
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl shadow-2xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="bg-white/10 rounded-md p-4">
                  <div className="flex gap-4 mb-3 items-center">
                    <PenTool className="text-white/70" />
                    <div className="text-sm">AI Design Assistant</div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-white/10 p-2 rounded h-4 w-3/4"></div>
                    <div className="bg-white/10 p-2 rounded h-4 w-1/2"></div>
                    <div className="bg-white/10 p-2 rounded h-4 w-5/6"></div>
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-xs text-white/70">Generating design...</div>
                    <Zap className="text-yellow-400 h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">How Designers Use Dessino.ai</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Seamlessly integrate AI-powered design generation into your workflow to enhance productivity without sacrificing creative control.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <PenTool className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Describe Your Vision</h3>
              <p className="text-gray-600">
                Start with a brief description of your design concept. Specify layout preferences, key components, and branding elements that matter most.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <Zap className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Generate & Refine</h3>
              <p className="text-gray-600">
                Review AI-generated designs and use simple instructions to refine layouts, adjust components, and perfect the details until they match your vision.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Layout className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Create User Flows</h3>
              <p className="text-gray-600">
                Connect multiple screens into interactive user flows to demonstrate the complete experience. Export HTML or share with stakeholders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Designers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Benefits for Designers</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your design process without compromising your creative vision or professional expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Designer Excellence Section - ADDED NEW SECTION */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Elevate Your Design Practice</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Take your professional capabilities to the next level with tools that enhance your natural talents
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {professionalBenefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-blue-100 flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-time Design Collaboration - NEW SECTION */}
      <section className="py-20 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transform Your Client Interactions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Move from static presentations to dynamic collaborative sessions
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="mb-10">
              <h3 className="text-2xl font-bold mb-6 text-blue-700 text-center">The Evolution of Client Presentations</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-gray-500 font-bold">1</span>
                    </div>
                    <h4 className="font-bold mt-2">Traditional</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      <span>Static mockups</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      <span>Limited interaction</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      <span>Feedback noted for later</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      <span>Days to implement changes</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-blue-700 font-bold">2</span>
                    </div>
                    <h4 className="font-bold mt-2">Interactive</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Clickable prototypes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>User flow demonstrations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Recorded feedback</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      <span>Hours to implement changes</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-purple-700 font-bold">3</span>
                    </div>
                    <h4 className="font-bold mt-2">With Dessino.ai</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span>Real-time modifications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span>Collaborative ideation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span>Immediate implementation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      <span>Minutes to implement changes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-8 mt-8">
              <div className="text-center max-w-3xl mx-auto">
                <h4 className="text-xl font-bold mb-3">Moving From Presentation to Collaboration</h4>
                <p className="text-gray-600">
                  When you can make real-time adjustments to designs during meetings, client interactions transform from review sessions into collaborative design sprints. This immediacy builds trust, demonstrates your expertise, and leads to better outcomes with fewer revision cycles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Scenarios */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Practical Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how designers are leveraging Dessino.ai in their daily work
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center mb-3">
                  <PlusCircle className="w-5 h-5 text-blue-500 mr-2" />
                  <h3 className="text-xl font-bold">{scenario.title}</h3>
                </div>
                <p className="text-gray-600">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Excellence Comparison - NEW SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Expand Your Design Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              How Dessino.ai enhances your existing design workflow
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-700">Traditional Design Process</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-blue-700">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Initial Concepting</h4>
                    <p className="text-gray-600 text-sm">Hours of sketching to explore basic layout options</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-blue-700">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Wireframing</h4>
                    <p className="text-gray-600 text-sm">Manual creation of basic structural elements for each screen</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-blue-700">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Detailed UI Design</h4>
                    <p className="text-gray-600 text-sm">Days spent crafting pixel-perfect designs for a limited set of screens</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-blue-700">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Prototyping</h4>
                    <p className="text-gray-600 text-sm">Additional time connecting screens and adding limited interactivity</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-700">With Dessino.ai</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-purple-700">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Idea Description</h4>
                    <p className="text-gray-600 text-sm">Minutes to communicate your design vision using simple descriptions</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-purple-700">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Multiple Design Generation</h4>
                    <p className="text-gray-600 text-sm">Instantly explore several distinct visual approaches simultaneously</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-purple-700">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Rapid Refinement</h4>
                    <p className="text-gray-600 text-sm">Quick iterations through simple text commands rather than manual tweaking</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="font-bold text-purple-700">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Comprehensive User Flows</h4>
                    <p className="text-gray-600 text-sm">Generate and connect many more screens to create truly complete experiences</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Enhance Your Design Process Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join designers who are using Dessino.ai to streamline their workflow and deliver exceptional results.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/signup" className="px-8 py-3 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors font-medium">
              Start Free Trial
            </Link>
          </div>
          <p className="mt-4 text-sm text-white/80">No credit card required. 10 free generations to get you started.</p>
        </div>
      </section>

      {/* Other Use Cases */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Explore Other Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how Dessino.ai helps different roles achieve their goals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link 
              to="/use-cases/product-managers"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For Product Managers</h3>
              <p className="text-gray-600 mb-4">
                Visualize features before development begins. Validate ideas with stakeholders using interactive prototypes.
              </p>
              <div className="text-blue-500 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
            
            <Link 
              to="/use-cases/startups"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For Startup Founders</h3>
              <p className="text-gray-600 mb-4">
                Create impressive prototypes for investor pitches and user testing without hiring a full design team.
              </p>
              <div className="text-blue-500 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default DesignersUseCase;